import { Icon } from 'components/Icon'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { useTranslation } from 'hooks/useTranslation'
import { ProductDetail } from 'models/Product'
import Trans from 'next-translate/Trans'

type ProductInformationProps = {
  product: Omit<ProductDetail, 'slug'> & {
    slug:
      | '5-lb-beef-patty-recipe'
      | '5-lb-chicken-patty-recipe'
      | '5-lb-venison-patty-recipe'
      | '5-lb-duck-patty-recipe'
      | '5-lb-lamb-patty-recipe'
      | '5-lb-turkey-patty-recipe'
  }
}

export const ProductInformation = ({ product }: ProductInformationProps) => {
  const { t } = useTranslation()

  const descriptionList = t(
    `e-commerce:detail.product.${product.slug}.information.list.items` as any,
    undefined,
    {
      returnObjects: true,
    }
  )

  return (
    <Stack gap="6" direction="column" css={{ color: '$primary-800' }}>
      <Stack gap="3" direction="column">
        <Text as="h2" size="h4" weight="bold">
          {t(`e-commerce:detail.product.${product.slug}.information.title`)}
        </Text>

        <Text as="p" size="headline">
          <Trans
            i18nKey={`e-commerce:detail.product.${product.slug}.information.description`}
            components={{ br: <br />, bold: <strong /> }}
          />
        </Text>

        {Array.isArray(descriptionList) && (
          <Text as="h3" size="h5" weight="medium">
            {t(`e-commerce:detail.product.${product.slug}.information.list.title` as any)}
          </Text>
        )}

        {Array.isArray(descriptionList) &&
          descriptionList?.map((item) => (
            <Stack key={item}>
              <Icon name="checkbox-circle-line" color="primary-500" size="3" />
              <Text as="p" size="headline">
                {item}
              </Text>
            </Stack>
          ))}
        {Array.isArray(descriptionList) && (
          <Text as="p" size="headline">
            {t(`e-commerce:detail.product.${product.slug}.information.list.wrap-up` as any)}
          </Text>
        )}
      </Stack>

      <Stack gap="3" direction="column">
        <Text as="h3" size="h4" weight="bold">
          {t(`e-commerce:detail.product.${product.slug}.benefits.title`)}
        </Text>

        <Text as="p" size="headline">
          {t(`e-commerce:detail.product.${product.slug}.benefits.description`)}
        </Text>

        <Text as="h3" size="h5" weight="medium">
          {t(`e-commerce:detail.product.${product.slug}.benefits.list.title`)}
        </Text>

        {t(`e-commerce:detail.product.${product.slug}.benefits.list.items` as any, undefined, {
          returnObjects: true,
        }).map((item) => (
          <Stack key={item}>
            <Icon name="checkbox-circle-line" color="primary-500" size="3" />
            <Text as="p" size="headline">
              {item}
            </Text>
          </Stack>
        ))}

        <Text as="p" size="headline">
          <Trans
            i18nKey={`e-commerce:detail.product.${product.slug}.benefits.list.wrap-up`}
            components={{ br: <br />, bold: <strong /> }}
          />
        </Text>
      </Stack>

      <Stack gap="3" direction="column">
        <Text as="h2" size="h4" weight="bold">
          {t(`e-commerce:detail.product.${product.slug}.balanced-nutrition.title`)}
        </Text>

        <Text as="p" size="headline">
          {t(`e-commerce:detail.product.${product.slug}.balanced-nutrition.description`)}
        </Text>
      </Stack>
    </Stack>
  )
}
